import React, { useState, useEffect, useMemo } from "react";
import { connect, ConnectedProps } from "react-redux";
import { RootState } from "../store/store";
import {
  Form,
  Button,
  Col,
  Row,
  Input,
  Space,
  Popconfirm,
  Tag,
  Divider,
} from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { queryApi } from "../store/actions/common";
import { queryClient } from "../store/queryClient";
import { triggerNotification } from "../store/directActions";
import CustomPaginatedTable from "../components/CustomPaginatedTable";
import PageHeader from "../components/PageHeader";
import { Statistic, Card } from "antd";
import { onBarcodeScan } from "../utils/barcodeEvents";
import useSocket from "../hooks/useSocket";

interface OwnProps {}

type EndShiftPageProps = OwnProps & PropsFromRedux;

const fetchScanMetaApi = async (scanCode: string) => {
  return queryApi(`ledger/scan_reset/${scanCode}`, "GET");
};

function EndShiftPage({}: EndShiftPageProps) {
  const navigate = useNavigate();
  const { sendMessage } = useSocket();
  const [form] = Form.useForm();
  const [verifiedSlots, setVerifiedSlots] = useState<string[]>([]);

  // Shift report data, sourced from the table below so we don't fire a second
  // request for the same list on mount. Stays undefined until the fetch lands
  // so the "No shift data available" submit guard still holds.
  const [endShiftData, setEndShiftData] = useState<any[] | undefined>(undefined);

  // Calculate instant sales from data
  const instantSales = useMemo(() => {
    if (!Array.isArray(endShiftData)) return 0;
    return endShiftData.reduce((sum, record) => {
      const isReverse = record.is_reverse !== 0;
      const total = isReverse
        ? (record.start_no -
            record.current_no +
            (record.is_sold && !record.is_paused ? 1 : 0)) *
          record.lottery?.amount
        : (record.current_no -
            record.start_no +
            (record.is_sold && !record.is_paused ? 1 : 0)) *
          record.lottery?.amount;
      return total + sum;
    }, 0);
  }, [endShiftData]);

  // Update form when instant sales changes
  useEffect(() => {
    if (typeof instantSales === "number") {
      form.setFieldsValue({ instant_sales: instantSales });
      onUpdateSalesData();
    }
  }, [instantSales]);

  const { mutate: fetchScanMeta, isPending: isScanning } = useMutation({
    mutationFn: (code: string) => fetchScanMetaApi(code),
    onSuccess: (scan_meta: any) => {
      console.log("scanned meta", scan_meta);

      if (scan_meta && !verifiedSlots.includes(scan_meta.slot_id)) {
        setVerifiedSlots([...verifiedSlots, scan_meta.slot_id]);
      }

      queryClient.invalidateQueries({
        queryKey: ["shift", "report", "endshift", { query: "" }],
      });
      sendMessage("screen-refresh");
    },
    onError: (error) => {
      triggerNotification({
        title: "Error",
        description: error.message || "Failed to scan code",
        type: "error",
      });
    },
  });

  // @ts-ignore
  useEffect(() => {
    return onBarcodeScan(async (data) => {
      // @ts-ignore
      if (data && data.trim()) fetchScanMeta(data);
    });
  }, []);

  // Mutation for marking ticket as sold
  const { mutate: markSold, isPending: isMarkingSold } = useMutation({
    mutationFn: (data: { scan_code: string; query: string }) =>
      queryApi(`ledger/scan?is_sold=1`, "POST", data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["shift", "report"],
      });
      sendMessage("screen-refresh");
      triggerNotification({
        title: "Success",
        description: "Ticket marked as sold successfully",
        type: "success",
      });
    },
    onError: (error: any) => {
      triggerNotification({
        title: "Error",
        description: error.message || "Failed to mark ticket as sold",
        type: "error",
      });
    },
  });

  // Mutation for creating end shift report
  const { mutate: createEndShift, isPending: isCreatingReport } = useMutation({
    mutationFn: (data: any) => queryApi(`report/dayclose`, "POST", data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["shift", "report"],
      });
      triggerNotification({
        title: "Success",
        description: "Shift report created successfully",
        type: "success",
      });
      navigate("/");
    },
    onError: (error: any) => {
      triggerNotification({
        title: "Error",
        description: error.message || "Failed to create shift report",
        type: "error",
      });
    },
  });

  const onUpdateItem = (data: any) => {
    markSold({
      scan_code: data.activated_code,
      query: "is_sold=1",
    });
  };

  const onUpdateSalesData = () => {
    const formData = form.getFieldsValue();
    const iS = parseInt(formData?.instant_sales ?? 0, 10) || 0;
    const iC = parseInt(formData?.instant_cashes ?? 0, 10) || 0;
    const oS = parseInt(formData?.online_sales ?? 0, 10) || 0;
    const oC = parseInt(formData?.online_cashes ?? 0, 10) || 0;
    const oCancel = parseInt(formData?.online_cancel ?? 0, 10) || 0;
    const rC = iS + oS - iC - oC - oCancel;
    form.setFieldsValue({ reg_cash: rC });
  };

  const onFormSubmit = (values: any) => {
    if (!Array.isArray(endShiftData)) {
      triggerNotification({
        title: "Error",
        description: "No shift data available",
        type: "error",
      });
      return;
    }

    const unverifiedCount = endShiftData.filter(
      (item) =>
        item.is_sold !== 1 &&
        item.is_closed !== 1 &&
        !verifiedSlots.includes(item.slot_id),
    ).length;

    // if (verifiedSlots.length < unverifiedCount) {
    //   triggerNotification({
    //     title: "Error",
    //     description: "Please verify all boxes by scanning the current ticket",
    //     type: "error",
    //   });
    //   return;
    // }

    createEndShift(values);
  };

  const computeShiftTotal = (record: any) => {
    const isReverse = record.is_reverse !== 0;
    const sold = record.is_sold && !record.is_paused ? 1 : 0;
    return isReverse
      ? (record.start_no - record.current_no + sold) * record.lottery?.amount
      : (record.current_no - record.start_no + sold) * record.lottery?.amount;
  };

  const endShiftColumns = [
    {
      title: <div style={{ minWidth: "max-content" }}>Box #</div>,
      dataIndex: "slot_number",
      key: "slot_number",
      render: (slot_number: number, record: any) => {
        const isVerified =
          record.is_sold ||
          record.is_closed ||
          verifiedSlots.includes(record.slot_id);
        return (
          <Tag
            color={isVerified ? "green" : "volcano"}
            style={{ padding: 10, width: "100%" }}
          >
            #{slot_number}
          </Tag>
        );
      },
      width: 70,
      sorter: (a: any, b: any) => (a.slot_number ?? 0) - (b.slot_number ?? 0),
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: (a: any, b: any) =>
        (a.lottery?.name ?? "").localeCompare(b.lottery?.name ?? ""),
      render: (_: any, record: any) => <div>{record.lottery?.name}</div>,
    },
    {
      title: <div style={{ minWidth: "max-content" }}>Game-Pack #</div>,
      dataIndex: "game",
      key: "game",
      sorter: (a: any, b: any) =>
        (a.lottery?.game_number ?? 0) - (b.lottery?.game_number ?? 0) ||
        (a.pack ?? 0) - (b.pack ?? 0),
      render: (_: any, record: any) => (
        <div style={{ minWidth: "max-content" }}>
          {record.lottery?.game_number}-{record.pack}
        </div>
      ),
    },
    {
      title: <div style={{ minWidth: "max-content" }}>Start #</div>,
      dataIndex: "start_no",
      key: "start_no",
      width: 100,
      sorter: (a: any, b: any) => (a.start_no ?? 0) - (b.start_no ?? 0),
    },
    {
      title: <div style={{ minWidth: "max-content" }}>End #</div>,
      dataIndex: "current_no",
      key: "current_no",
      width: 100,
      sorter: (a: any, b: any) => (a.current_no ?? 0) - (b.current_no ?? 0),
    },
    {
      title: "Value",
      dataIndex: "lottery",
      key: "lottery",
      sorter: (a: any, b: any) =>
        (a.lottery?.amount ?? 0) - (b.lottery?.amount ?? 0),
      render: (lottery: any) => (
        <div>
          {lottery?.currency}
          {lottery?.amount}
        </div>
      ),
      width: 100,
    },
    {
      title: "Total",
      dataIndex: "total",
      key: "total",
      sorter: (a: any, b: any) => computeShiftTotal(a) - computeShiftTotal(b),
      render: (_: any, record: any) => {
        const total = computeShiftTotal(record);
        return (
          <div>
            {record.lottery?.currency}
            {total}
          </div>
        );
      },
      width: 100,
    },
    {
      title: "Current Status",
      key: "is_sold",
      render: (_: any, record: any) => (
        <Space size="middle">
          {record.is_sold || record.is_closed ? (
            <div>{record.is_closed ? "Discontinued" : "Sold"}</div>
          ) : (
            <Popconfirm
              title="Are you sure to mark this sold?"
              onConfirm={() => onUpdateItem(record)}
              onCancel={() => {}}
              okText="Yes"
              cancelText="No"
              disabled={isMarkingSold}
            >
              <a href="#" style={{ color: "red" }}>
                Mark Sold
              </a>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <section id="end-shift" className="page-section h-page p-6 flex flex-col">
      <PageHeader
        title="End Shift"
        extra={[
          <Button
            key="back"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(-1)}
          >
            Back
          </Button>,
          <Popconfirm
            key="report"
            title="Are you sure to get report?"
            onConfirm={() => form.submit()}
            onCancel={() => {}}
            okText="Yes"
            cancelText="No"
          >
            <Button
              loading={isCreatingReport}
              danger
              type="primary"
              onClick={() => {}}
            >
              Get Report
            </Button>
          </Popconfirm>,
        ]}
      />

      <div className="shift-stats app-surface-alt rounded-xl p-6 mb-6">
        <Form
          form={form}
          onFinish={onFormSubmit}
          autoComplete="off"
          layout="inline"
          hideRequiredMark
          style={{ width: "100%" }}
        >
          <Row gutter={16} style={{ width: "100%" }}>
            <Col span={4}>
              <Card>
                <Statistic
                  title="Instant Sales"
                  value={instantSales}
                  formatter={(value) => (
                    <Form.Item name="instant_sales" style={{ margin: 0 }}>
                      <Input
                        placeholder="0"
                        disabled
                        bordered={false}
                        style={{
                          fontWeight: 600,
                          fontSize: "1.5rem",
                          padding: 0,
                          textAlign: "center",
                        }}
                        value={instantSales}
                      />
                    </Form.Item>
                  )}
                />
              </Card>
            </Col>
            <Col span={5}>
              <Card>
                <Statistic
                  title="Instant Cashes"
                  formatter={() => (
                    <Form.Item
                      name="instant_cashes"
                      rules={[
                        { required: true, message: "Please enter value" },
                      ]}
                      style={{ margin: 0 }}
                    >
                      <Input
                        placeholder="0"
                        bordered={false}
                        style={{
                          fontWeight: 600,
                          fontSize: "1.5rem",
                          padding: 0,
                          textAlign: "center",
                        }}
                        onChange={onUpdateSalesData}
                        type="number"
                        autoComplete="off"
                      />
                    </Form.Item>
                  )}
                />
              </Card>
            </Col>
            <Col span={5}>
              <Card>
                <Statistic
                  title="Online Sales"
                  formatter={() => (
                    <Form.Item
                      name="online_sales"
                      rules={[
                        { required: true, message: "Please enter value" },
                      ]}
                      style={{ margin: 0 }}
                    >
                      <Input
                        placeholder="0"
                        bordered={false}
                        style={{
                          fontWeight: 600,
                          fontSize: "1.5rem",
                          padding: 0,
                          textAlign: "center",
                        }}
                        onChange={onUpdateSalesData}
                        type="number"
                        autoComplete="off"
                      />
                    </Form.Item>
                  )}
                />
              </Card>
            </Col>
            <Col span={5}>
              <Card>
                <Statistic
                  title="Online Cashes"
                  formatter={() => (
                    <Form.Item
                      name="online_cashes"
                      rules={[
                        { required: true, message: "Please enter value" },
                      ]}
                      style={{ margin: 0 }}
                    >
                      <Input
                        placeholder="0"
                        bordered={false}
                        style={{
                          fontWeight: 600,
                          fontSize: "1.5rem",
                          padding: 0,
                          textAlign: "center",
                        }}
                        onChange={onUpdateSalesData}
                        type="number"
                        autoComplete="off"
                      />
                    </Form.Item>
                  )}
                />
              </Card>
            </Col>
            <Col span={5}>
              <Card>
                <Statistic
                  title="Online Cancel"
                  formatter={() => (
                    <Form.Item
                      name="online_cancel"
                      rules={[
                        { required: true, message: "Please enter value" },
                      ]}
                      style={{ margin: 0 }}
                    >
                      <Input
                        placeholder="0"
                        bordered={false}
                        style={{
                          fontWeight: 600,
                          fontSize: "1.5rem",
                          padding: 0,
                          textAlign: "center",
                        }}
                        onChange={onUpdateSalesData}
                        type="number"
                        autoComplete="off"
                      />
                    </Form.Item>
                  )}
                />
              </Card>
            </Col>
          </Row>
        </Form>
      </div>

      <div
        className="app-surface rounded-xl shadow-sm border border-solid app-border overflow-hidden flex flex-col flex-1 p-6 mb-6"
        style={{ minHeight: 620 }}
      >
        <h3 className="text-xl font-bold app-text mb-4">
          Unverified / Unsold Items
        </h3>
        {/* @ts-ignore */}
        <CustomPaginatedTable
          columns={endShiftColumns}
          hidePagination={true}
          filterData={{}}
          queryKey={["shift", "report", "endshift", { query: "" }]}
          path={"report/endshift?query="}
          // Both tables share this query key, so one request covers both.
          // Receives the unfiltered list, which is what the sales total and the
          // submit guard need.
          getDataStats={setEndShiftData}
          localFilterFunction={(data: any[]) => {
            if (!Array.isArray(data)) return [];
            return data.filter(
              (item) =>
                item.is_sold !== 1 &&
                item.is_closed !== 1 &&
                !verifiedSlots.includes(item.slot_id),
            );
          }}
          tableProps={{
            sticky: true,
            tableLayout: "auto",
            scroll: { y: true, x: true },
            loading: isMarkingSold,
            rowKey: (record: any) => record.slot_id || record.id,
          }}
        />
      </div>

      <Divider />

      <div
        className="app-surface rounded-xl shadow-sm border border-solid app-border overflow-hidden flex flex-col flex-1 p-6"
        style={{ minHeight: 620 }}
      >
        <h3 className="text-xl font-bold app-text mb-4">
          Verified / Sold Items
        </h3>
        {/* @ts-ignore */}
        <CustomPaginatedTable
          columns={endShiftColumns}
          hidePagination={true}
          filterData={{}}
          queryKey={["shift", "report", "endshift", { query: "" }]}
          path={"report/endshift?query="}
          localFilterFunction={(data: any[]) => {
            if (!Array.isArray(data)) return [];
            return data.filter(
              (item) =>
                item.is_sold === 1 ||
                item.is_closed === 1 ||
                verifiedSlots.includes(item.slot_id),
            );
          }}
          tableProps={{
            sticky: true,
            tableLayout: "auto",
            scroll: { y: true, x: true },
            loading: isMarkingSold,
            rowKey: (record: any) => record.slot_id || record.id,
          }}
        />
      </div>
    </section>
  );
}

const mapStateToProps = (state: RootState) => ({
  scan_meta: (state as any).inventory?.scan_meta,
});

const mapDispatchToProps = () => ({});

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(EndShiftPage);
